/**
 * 表单验证模式定义
 * 使用 Zod 定义所有表单的验证规则
 */

import { z } from 'zod';

/**
 * 认证表单验证模式
 * 登录时 username 不展示，表单项仍为 ''，需允许空串否则校验会拦下登录请求
 */
export const authSchema = z.object({
  email: z.string().email('请输入有效的邮箱地址'),
  password: z.string().min(6, '密码至少6位'),
  username: z.union([z.string().min(1, '用户名不能为空'), z.literal('')]).optional(),
});

export type AuthFormData = z.infer<typeof authSchema>;

/**
 * 工作区表单验证模式
 */
export const workspaceSchema = z.object({
  name: z.string().min(1, '工作区名称不能为空').max(100, '工作区名称不能超过100个字符'),
});

export type WorkspaceFormData = z.infer<typeof workspaceSchema>;

/**
 * 书签表单验证模式
 */
export const bookmarkSchema = z.object({
  name: z.string().min(1, '名称不能为空').max(200, '名称不能超过200个字符'),
  url: z.string().min(1, '网址不能为空'),
  note: z.string().max(1000, '备注不能超过1000个字符').optional(),
});

export type BookmarkFormData = z.infer<typeof bookmarkSchema>;

/**
 * 用户名验证模式
 */
export const usernameSchema = z.object({
  username: z.string().min(1, '用户名不能为空').max(50, '用户名不能超过50个字符'),
});

export type UsernameFormData = z.infer<typeof usernameSchema>;

/**
 * 分组表单验证模式
 */
export const groupSchema = z.object({
  name: z.string().min(1, '分组名称不能为空').max(100, '分组名称不能超过100个字符'),
});

export type GroupFormData = z.infer<typeof groupSchema>;

/**
 * 邀请成员表单验证模式
 */
export const inviteMemberSchema = z.object({
  email: z.string().email('请输入有效的邮箱地址'),
});

export type InviteMemberFormData = z.infer<typeof inviteMemberSchema>;

/**
 * 待办事项重复类型
 */
export const recurrenceTypeSchema = z.enum([
  'daily',
  'weekday',
  'weekly',
  'monthly',
  'monthly-last-day',
  'quarterly',
  'yearly',
  'lunar-yearly',
  'legal-workday',
  'ebbinghaus',
  'custom-daily',
  'custom-weekly',
  'custom-monthly',
  'custom-yearly',
]);

/**
 * 重复频次类型
 */
export const recurrenceFrequencyTypeSchema = z.enum(['unlimited', 'endDate', 'count']);

/**
 * 待办事项表单验证模式
 */
export const todoSchema = z.object({
  title: z.string().min(1, '标题不能为空').max(200, '标题不能超过200个字符'),
  description: z.string().max(1000, '描述不能超过1000个字符').optional(),
  selectedGroupId: z.string().min(1, '必须选择一个分组'),
  fontColor: z.string().regex(/^#[0-9A-F]{6}$/i, '颜色格式不正确').optional(),
  recurrenceType: recurrenceTypeSchema.optional(),
  recurrenceFrequencyType: recurrenceFrequencyTypeSchema.optional(),
  endDate: z.date().nullable().optional(),
  recurrenceCount: z.number().min(1, '重复次数至少为1').max(1000, '重复次数不能超过1000').optional(),
  reminderEnabled: z.boolean().optional(),
  reminderMinutes: z.array(z.number()).optional(),
  assignedTo: z.array(z.string()).optional(),
});

export type TodoFormData = z.infer<typeof todoSchema>;
